import { useState } from 'react';
import { apiPost } from '../../api/client';
import { Button } from '../../design-system';
import { tokens } from '../../design-system/tokens';
import { LEAD_STATE_PASTELS, nextPastel, pastelFromBackground } from './leadPastels';

const CLAUDE_MODELS = [
  { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
  { id: 'claude-haiku-5-5', label: 'Claude Haiku 5.5' },
  { id: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5' },
  { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
  { id: 'claude-opus-4-7', label: 'Claude Opus 4.7' },
];

type StateRow = { id: string; label: string; color: string; rule: string };
type ActionRow = { id: string; idLead: string; tiempoMin: number; instruccion: string };

type Props = {
  agentId: string;
  token: string | null;
  values: Record<string, unknown>;
  disabled?: boolean;
  onChange: (next: Record<string, unknown>) => void;
};

function asStates(value: unknown): StateRow[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item, index) => {
    if (!item || typeof item !== 'object') return [];
    const row = item as { id?: unknown; label?: unknown; color?: unknown; rule?: unknown };
    const id = typeof row.id === 'string' ? row.id : '';
    const label = typeof row.label === 'string' ? row.label : '';
    if (!id && !label) return [];
    const rule = typeof row.rule === 'string' ? row.rule.slice(0, 500) : '';
    return [{ id, label, color: pastelFromBackground(typeof row.color === 'string' ? row.color : '', index).background, rule }];
  });
}

function asActions(value: unknown): ActionRow[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const row = item as Partial<ActionRow>;
    return [
      {
        id: typeof row.id === 'string' ? row.id : '',
        idLead: typeof row.idLead === 'string' ? row.idLead : '',
        tiempoMin: typeof row.tiempoMin === 'number' ? row.tiempoMin : 60,
        instruccion: typeof row.instruccion === 'string' ? row.instruccion : '',
      },
    ];
  });
}

function slug(label: string): string {
  const base = label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);
  return base || 'estado';
}

function uniqueId(base: string, used: string[]): string {
  let id = base || 'estado';
  let n = 2;
  while (used.includes(id)) id = `${base}-${n++}`.slice(0, 40);
  return id;
}

const fieldStyle = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  fontSize: 14,
  fontFamily: 'inherit',
} as const;

export function LeadQualificationSettings({ agentId, token, values, disabled, onChange }: Props) {
  const mode = values.mode === 'internal' ? 'internal' : 'ads';
  const model = typeof values.model === 'string' && values.model.startsWith('claude-') ? values.model : CLAUDE_MODELS[0].id;
  const interval = typeof values.intervalMinutes === 'number' ? values.intervalMinutes : 10;
  const sampleSize = typeof values.schemaSampleSize === 'number' && values.schemaSampleSize >= 1 ? values.schemaSampleSize : 10;
  const icpHint = typeof values.icpHint === 'string' ? values.icpHint : '';
  const states = asStates(values.states);
  const actions = asActions(values.actions);
  const [generating, setGenerating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const patch = (partial: Record<string, unknown>) => onChange({ ...values, ...partial });

  const generate = async () => {
    if (!token || disabled) return;
    setGenerating(true);
    setNotice(null);
    try {
      const data = await apiPost<{ states: StateRow[] }>(
        '/api/console/lead-qualification/generate-schema',
        { agentId, model, sampleSize },
        token,
      );
      patch({ states: data.states });
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 13, color: tokens.colors.neutral[700] }}>Modo</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {(
            [
              { id: 'ads', label: 'Ads de terceros' },
              { id: 'internal', label: 'Interno' },
            ] as const
          ).map((item) => {
            const active = mode === item.id;
            return (
              <button
                key={item.id}
                type="button"
                disabled={disabled}
                onClick={() => patch({ mode: item.id })}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: active ? '1px solid #7c3aed' : '1px solid #e2e8f0',
                  background: active ? '#f5f3ff' : '#fff',
                  color: active ? '#5b21b6' : '#334155',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: disabled ? 'default' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </div>

      {mode === 'ads' ? (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: tokens.colors.neutral[700] }}>
          Perfil ideal (ICP)
          <textarea
            style={{ ...fieldStyle, minHeight: 96 }}
            disabled={disabled}
            maxLength={4000}
            value={icpHint}
            onChange={(e) => patch({ icpHint: e.target.value })}
          />
        </label>
      ) : (
        <>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: tokens.colors.neutral[700] }}>
            Modelo
            <select style={fieldStyle} disabled={disabled} value={model} onChange={(e) => patch({ model: e.target.value })}>
              {CLAUDE_MODELS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: tokens.colors.neutral[700] }}>
            Cada cuántos minutos se califica
            <input
              type="number"
              min={1}
              max={1440}
              style={fieldStyle}
              disabled={disabled}
              value={interval}
              onChange={(e) => patch({ intervalMinutes: Number(e.target.value) || 10 })}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: tokens.colors.neutral[700] }}>
            Últimas conversaciones para generar
            <input
              type="number"
              min={1}
              max={100}
              style={fieldStyle}
              disabled={disabled}
              value={sampleSize}
              onChange={(e) => patch({ schemaSampleSize: Number(e.target.value) || 10 })}
            />
          </label>
          <div>
            <Button type="button" variant="outline" onClick={() => void generate()} loading={generating} disabled={disabled || generating}>
              Generar clasificación
            </Button>
            <p style={{ margin: '8px 0 0', fontSize: 12, color: tokens.colors.neutral[500] }}>
              Usa las últimas {sampleSize} conversaciones del agente. Si hay menos, no se genera y hay que cargar los estados a mano.
            </p>
            {notice ? <p style={{ margin: '8px 0 0', fontSize: 13, color: tokens.colors.error.dark }}>{notice}</p> : null}
            {states.length < 2 ? (
              <p style={{ margin: '8px 0 0', fontSize: 13, color: tokens.colors.error.dark }}>
                El modo interno no funciona hasta que haya al menos 2 estados.
              </p>
            ) : null}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <strong style={{ fontSize: 13 }}>Estados</strong>
            {states.map((state, index) => {
              const pastel = pastelFromBackground(state.color, index);
              const chipLabel = state.label.trim() || 'Nombre';
              return (
              <div key={`${state.id}-${index}`} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  type="button"
                  title="Cambiar color"
                  aria-label="Cambiar color"
                  disabled={disabled}
                  onClick={() => {
                    const current = LEAD_STATE_PASTELS.findIndex((item) => item.background === pastel.background);
                    const nextColor = LEAD_STATE_PASTELS[(current + 1) % LEAD_STATE_PASTELS.length];
                    const next = states.slice();
                    next[index] = { ...state, color: nextColor.background };
                    patch({ states: next });
                  }}
                  style={{
                    width: 18,
                    height: 18,
                    flex: '0 0 auto',
                    padding: 0,
                    borderRadius: 999,
                    border: `2px solid ${pastel.color}`,
                    background: pastel.background,
                    cursor: disabled ? 'default' : 'pointer',
                  }}
                />
                <input
                  aria-label="Nombre del estado"
                  disabled={disabled}
                  value={state.label}
                  placeholder="Nombre"
                  size={Math.min(28, Math.max(chipLabel.length, 8))}
                  onChange={(e) => {
                    const next = states.slice();
                    const label = e.target.value;
                    const used = states.filter((_, i) => i !== index).map((s) => s.id);
                    const id = state.label.trim() ? state.id : uniqueId(slug(label), used);
                    next[index] = { label, id, color: state.color, rule: state.rule };
                    const nextActions =
                      id === state.id
                        ? actions
                        : actions.map((action) => (action.idLead === state.id ? { ...action, idLead: id } : action));
                    patch({ states: next, actions: nextActions });
                  }}
                  style={{
                    width: 'auto',
                    maxWidth: '40%',
                    minWidth: 0,
                    boxSizing: 'content-box',
                    border: '1px solid transparent',
                    borderRadius: 999,
                    padding: '6px 12px',
                    background: pastel.background,
                    color: pastel.color,
                    fontSize: 12,
                    fontWeight: 700,
                    fontFamily: 'inherit',
                  }}
                />
                <input
                  aria-label="Regla"
                  style={{ ...fieldStyle, flex: 1, width: 'auto', minWidth: 0 }}
                  disabled={disabled}
                  value={state.rule}
                  maxLength={500}
                  placeholder="Regla: el visitante escribió una vez, el agente respondió y no volvió a escribir"
                  onChange={(e) => {
                    const next = states.slice();
                    next[index] = { ...state, rule: e.target.value.slice(0, 500) };
                    patch({ states: next });
                  }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  disabled={disabled}
                  onClick={() => patch({ states: states.filter((_, i) => i !== index) })}
                >
                  Borrar
                </Button>
              </div>
              );
            })}
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              onClick={() => {
                const id = uniqueId('estado', states.map((s) => s.id));
                patch({
                  states: [...states, { id, label: '', rule: '', color: nextPastel(states.map((s) => s.color)).background }],
                });
              }}
            >
              Agregar estado
            </Button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <strong style={{ fontSize: 13 }}>Acciones</strong>
            {actions.map((action, index) => (
              <div key={`${action.id}-${index}`} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <select
                  style={fieldStyle}
                  disabled={disabled}
                  value={action.idLead}
                  onChange={(e) => {
                    const next = actions.slice();
                    next[index] = { ...action, idLead: e.target.value };
                    patch({ actions: next });
                  }}
                >
                  <option value="">Estado que la dispara</option>
                  {states.filter((s) => s.id && s.label).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min={1}
                  style={fieldStyle}
                  disabled={disabled}
                  value={action.tiempoMin}
                  onChange={(e) => {
                    const next = actions.slice();
                    next[index] = { ...action, tiempoMin: Number(e.target.value) || 1 };
                    patch({ actions: next });
                  }}
                />
                <textarea
                  style={{ ...fieldStyle, minHeight: 72 }}
                  disabled={disabled}
                  placeholder="Instrucción para el modelo"
                  value={action.instruccion}
                  onChange={(e) => {
                    const next = actions.slice();
                    next[index] = { ...action, instruccion: e.target.value };
                    patch({ actions: next });
                  }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  disabled={disabled}
                  onClick={() => patch({ actions: actions.filter((_, i) => i !== index) })}
                >
                  Borrar acción
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              disabled={disabled || states.length === 0}
              onClick={() =>
                patch({
                  actions: [
                    ...actions,
                    {
                      id: uniqueId('accion', actions.map((a) => a.id)),
                      idLead: states[0]?.id ?? '',
                      tiempoMin: 60,
                      instruccion: '',
                    },
                  ],
                })
              }
            >
              Agregar acción
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
